
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { MeasurementTypeSelect } from "./MeasurementTypeSelect";
import { UnitSelect } from "./UnitSelect";
import { ValueInput } from "./ValueInput";
import { ConvertButton } from "./ConvertButton";
import { formatConversionResult, validateConversion } from "./conversion-utils";

interface UnitConverterFormProps {
  onMeasurementTypeChange: (type: string) => void;
}

export function UnitConverterForm({ onMeasurementTypeChange }: UnitConverterFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [measurementType, setMeasurementType] = useState<string>("");
  const [value, setValue] = useState("");
  const [fromUnit, setFromUnit] = useState("");
  const [toUnit, setToUnit] = useState("");

  useEffect(() => {
    // Check for initial state from navigation
    const state = location.state as { selectedType?: string; selectedUnit?: string } | null;
    if (state?.selectedType) {
      setMeasurementType(state.selectedType);
      onMeasurementTypeChange(state.selectedType);
      if (state.selectedUnit) {
        setFromUnit(state.selectedUnit);
      }
      // Clear the navigation state to prevent it from persisting
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, onMeasurementTypeChange]);

  const handleMeasurementTypeChange = (type: string) => {
    setMeasurementType(type);
    onMeasurementTypeChange(type);
    setFromUnit("");
    setToUnit("");
  };

  const handleConvert = () => {
    const validationError = validateConversion(value, fromUnit, toUnit);
    
    if (validationError) {
      toast({
        title: "Invalid Input",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    try {
      const formattedResult = formatConversionResult(Number(value), fromUnit, toUnit);
      navigate('/results', { 
        state: { 
          result: formattedResult,
          measurementType,
          fromUnit,
          toUnit
        } 
      });
    } catch (error) {
      toast({
        title: "Conversion Error",
        description: error instanceof Error ? error.message : "An error occurred during conversion",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <MeasurementTypeSelect 
          value={measurementType} 
          onValueChange={handleMeasurementTypeChange} 
        />

        {measurementType && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <UnitSelect
              measurementType={measurementType}
              value={fromUnit}
              onValueChange={setFromUnit}
              label="From Unit"
              placeholder="Convert from unit"
            />
            <UnitSelect
              measurementType={measurementType}
              value={toUnit}
              onValueChange={setToUnit}
              label="To Unit"
              placeholder="Convert to unit"
            />
          </div>
        )}

        {fromUnit && toUnit && (
          <div className="space-y-4">
            <ValueInput value={value} onChange={setValue} />
            <ConvertButton onClick={handleConvert} />
          </div>
        )}
      </div>
    </div>
  );
}
